import type { AgentTask, ExecutorProfile, TaskType } from "../types";
import { TASK_TYPES } from "../types";
import { isTaskType } from "./tasks";
import { formatWhen } from "../lib/format";

export const WAKE_EVENTS = ["task.created", "task.rewake", "executor.test"] as const;

export type WakeEvent = (typeof WAKE_EVENTS)[number];

export interface WakePayload {
  event: WakeEvent;
  taskId: string | null;
  type: string | null;
  apiBase: string;
  taskUrl: string | null;
  createdAt: string;
}

export interface ExecutorInput {
  name: string;
  wakeUrl: string;
  headerName: string;
  headerValue: string;
  taskTypes: string[];
}

export type ExecutorInputError = "name" | "url" | "header" | "types";

const HEADER_NAME = /^[A-Za-z0-9-]{1,40}$/;
const BLOCKED_HEADERS = new Set(["host", "content-length", "transfer-encoding", "connection", "content-type"]);

export function parseTaskTypes(value: unknown): TaskType[] {
  if (!Array.isArray(value)) return [];
  const next: TaskType[] = [];
  for (const item of value) {
    if (typeof item === "string" && isTaskType(item) && !next.includes(item)) next.push(item);
  }
  return next;
}

export function isHttpsUrl(value: string): boolean {
  try {
    const url = new URL(value.trim());
    return url.protocol === "https:" && !url.username && !url.password;
  } catch {
    return false;
  }
}

export function headerNameError(name: string): boolean {
  const trimmed = name.trim();
  if (!trimmed) return false;
  return !HEADER_NAME.test(trimmed) || BLOCKED_HEADERS.has(trimmed.toLowerCase());
}

/** headerValueOptional lets an edit leave the stored secret untouched. */
export function validateExecutorInput(
  input: ExecutorInput,
  headerValueOptional = false,
): { value: ExecutorInput & { taskTypes: TaskType[] } } | { error: ExecutorInputError } {
  const name = input.name.trim().slice(0, 40);
  const wakeUrl = input.wakeUrl.trim().slice(0, 500);
  const headerName = input.headerName.trim().slice(0, 40);
  const headerValue = input.headerValue.trim().slice(0, 500);
  const taskTypes = parseTaskTypes(input.taskTypes);
  if (!name) return { error: "name" };
  if (!isHttpsUrl(wakeUrl)) return { error: "url" };
  if (headerNameError(headerName)) return { error: "header" };
  if (!headerName && headerValue) return { error: "header" };
  if (headerName && !headerValue && !headerValueOptional) return { error: "header" };
  if (!taskTypes.length) return { error: "types" };
  return { value: { name, wakeUrl, headerName, headerValue, taskTypes } };
}

export function typeCovered(executors: readonly Pick<ExecutorProfile, "enabled" | "taskTypes">[], type: string): boolean {
  return executors.some((item) => item.enabled && item.taskTypes.includes(type as TaskType));
}

export function queuedArrivals(before: readonly AgentTask[] | null, after: readonly AgentTask[] | null): AgentTask[] {
  if (!before || !after) return [];
  const known = new Set(before.map((task) => task.id));
  return after.filter((task) => !known.has(task.id) && task.status === "排队");
}

export function wakePayload(input: {
  event: WakeEvent;
  taskId?: string | null;
  type?: string | null;
  origin: string;
  createdAt: string;
}): WakePayload {
  const origin = input.origin.replace(/\/$/, "");
  const taskId = input.taskId?.trim() ? input.taskId.trim() : null;
  return {
    event: input.event,
    taskId,
    type: input.type ?? null,
    apiBase: `${origin}/api/agent`,
    taskUrl: taskId ? `${origin}/api/agent/tasks/${encodeURIComponent(taskId)}` : null,
    createdAt: input.createdAt,
  };
}

export function executorStatusLine(executor: Pick<
  ExecutorProfile,
  "enabled" | "taskTypes" | "lastWakeAt" | "lastWakeStatus" | "lastWakeError" | "lastClaimAt"
>): string {
  const types = executor.taskTypes.join("、") || "没有分配任务";
  const wake = !executor.lastWakeAt
    ? "还没有唤醒过"
    : executor.lastWakeStatus
      ? `上次唤醒 ${formatWhen(executor.lastWakeAt)}，HTTP ${executor.lastWakeStatus}`
      : `上次唤醒 ${formatWhen(executor.lastWakeAt)}，没有连上${executor.lastWakeError ? `：${executor.lastWakeError}` : ""}`;
  const claim = executor.lastClaimAt ? `上次领取 ${formatWhen(executor.lastClaimAt)}` : "还没有领过任务";
  const prefix = executor.enabled ? "" : "已停用。";
  return `${prefix}负责 ${types}。${wake}。${claim}。`;
}

export function wakeResultLine(results: readonly { name: string; status: number | null; error: string }[]): string {
  if (!results.length) return "没有负责这类任务的执行者。";
  return results
    .map((item) => (item.status ? `${item.name}：HTTP ${item.status}` : `${item.name}：没有连上${item.error ? `（${item.error}）` : ""}`))
    .join("；");
}

export function generateExecutorKey(): string {
  const bytes = new Uint8Array(32);
  crypto.getRandomValues(bytes);
  let binary = "";
  for (const byte of bytes) binary += String.fromCharCode(byte);
  const body = btoa(binary).replaceAll("+", "-").replaceAll("/", "_").replaceAll("=", "");
  return `mdx_${body}`;
}

export function keyHint(key: string): string {
  if (key.length < 12) return "已生成";
  return `${key.slice(0, 7)}…${key.slice(-4)}`;
}

export async function hashExecutorKey(key: string): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(key));
  return [...new Uint8Array(digest)].map((byte) => byte.toString(16).padStart(2, "0")).join("");
}

export type AgentAccess =
  | { kind: "secret" }
  | { kind: "executor"; userId: string; executorId: string; taskTypes: TaskType[] }
  | { kind: "denied"; reason: "missing" | "rejected" | "disabled" };

export function decideAgentAccess(input: {
  secretAccepted: boolean;
  executor: { userId: string; id: string; taskTypes: readonly string[]; enabled: boolean } | null;
  tokenPresent: boolean;
}): AgentAccess {
  if (input.secretAccepted) return { kind: "secret" };
  if (!input.tokenPresent) return { kind: "denied", reason: "missing" };
  if (!input.executor) return { kind: "denied", reason: "rejected" };
  if (!input.executor.enabled) return { kind: "denied", reason: "disabled" };
  return {
    kind: "executor",
    userId: input.executor.userId,
    executorId: input.executor.id,
    taskTypes: parseTaskTypes([...input.executor.taskTypes]),
  };
}

export function executorMayTouch(
  access: AgentAccess,
  task: { userId: string; type: string; claimedBy?: string | null },
  action: "list" | "claim" | "finish",
): boolean {
  if (access.kind === "secret") return true;
  if (access.kind !== "executor") return false;
  if (task.userId !== access.userId) return false;
  if (access.taskTypes.includes(task.type as TaskType)) return true;
  return action === "finish" && task.claimedBy === access.executorId;
}

export function normalizeExecutor(value: unknown): ExecutorProfile | null {
  if (!value || typeof value !== "object") return null;
  const raw = value as Partial<ExecutorProfile>;
  if (typeof raw.id !== "string" || typeof raw.name !== "string" || typeof raw.wakeUrl !== "string") return null;
  if (typeof raw.updatedAt !== "string") return null;
  const taskTypes = parseTaskTypes(raw.taskTypes);
  return {
    id: raw.id,
    name: raw.name.slice(0, 40),
    wakeUrl: raw.wakeUrl.slice(0, 500),
    headerName: typeof raw.headerName === "string" ? raw.headerName.slice(0, 40) : "",
    headerSet: raw.headerSet === true,
    taskTypes,
    keyHint: typeof raw.keyHint === "string" ? raw.keyHint.slice(0, 40) : "",
    enabled: raw.enabled !== false,
    lastWakeAt: typeof raw.lastWakeAt === "string" ? raw.lastWakeAt : null,
    lastWakeStatus: typeof raw.lastWakeStatus === "number" && Number.isFinite(raw.lastWakeStatus) ? raw.lastWakeStatus : null,
    lastWakeError: typeof raw.lastWakeError === "string" ? raw.lastWakeError.slice(0, 200) : "",
    lastClaimAt: typeof raw.lastClaimAt === "string" ? raw.lastClaimAt : null,
    createdAt: typeof raw.createdAt === "string" ? raw.createdAt : raw.updatedAt,
    updatedAt: raw.updatedAt,
  };
}

export function allTaskTypes(): readonly TaskType[] {
  return TASK_TYPES;
}
